import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";

import { useAspSelector } from "../../../../test/jest-redux-hooks";
import { flowsGet, flowsList } from "../../../../modules/channels/api/Api";

import Select from "components/commonComponents/select/index";
import BtnInput from "./BtnInput";

import DocSvg from "assets/customSVG/DocSvg";
import DocumentIcon from "assets/customSVG/DocumentIcon";
import Tag from "assets/customSVG/Tag";
import ReviewIcon from "assets/customSVG/ReviewIcon";
import Delete from "assets/customSVG/Delete";
import { ICONS } from "assets/icons";

const DefaultFlowIcon = ({ color }) => (
  <DocSvg width="14" height="14" color={color} />
);

const DocumentFlowIcon = ({ color }) => (
  <DocumentIcon width="14" height="14" style={{ color }} />
);

const PromotionFlowIcon = ({ fill }) => (
  <Tag width="14" height="14" fill={fill} />
);

const ReviewFlowIcon = ({ color }) => (
  <ReviewIcon width="14" height="14" color={color} />
);

const FLOW_ICON_OPTIONS = [
  // {
  //   label: "Default",
  //   value: "DEFAULT",
  //   icon1: DefaultFlowIcon,
  // },
  {
    label: "Document",
    value: "DOCUMENT",
    icon1: DocumentFlowIcon,
  },
  {
    label: "Promotion",
    value: "PROMOTION",
    icon1: PromotionFlowIcon,
  },
  {
    label: "Review",
    value: "REVIEW",
    icon1: ReviewFlowIcon,
  },
];

const parseFlowJson = (raw) => {
  if (!raw) return null;

  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error("Unable to parse flow_json string", error);
      return null;
    }
  }

  if (typeof raw === "object") {
    try {
      return JSON.parse(JSON.stringify(raw));
    } catch (error) {
      console.error("Unable to clone flow_json object", error);
    }
    return raw;
  }

  return null;
};

const resolveFirstScreenId = (flowJson) => {
  if (!flowJson) return null;

  if (Array.isArray(flowJson?.screens) && flowJson.screens.length > 0) {
    return flowJson.screens[0]?.id || null;
  }

  if (
    Array.isArray(flowJson?.data?.screens) &&
    flowJson.data.screens.length > 0
  ) {
    return flowJson.data.screens[0]?.id || null;
  }

  if (Array.isArray(flowJson?.screen_order) && flowJson.screen_order.length) {
    return flowJson.screen_order[0] || null;
  }

  if (typeof flowJson?.entry_point?.next?.name === "string") {
    return flowJson.entry_point.next.name;
  }

  return null;
};

const formatFlowOption = (flow) => {
  const identifier = flow?.id ?? flow?.flow_id ?? flow?.uuid;
  return {
    label: flow?.name ?? `Flow ${identifier ?? ""}`,
    value: String(identifier ?? ""),
    identifier: String(identifier ?? ""),
    data: flow,
  };
};

const FlowCTAButton = ({
  data,
  allow,
  optionsList,
  typeLabel,
  onTypeChange,
  updateButtons,
}) => {
  const { currentBrand } = useAspSelector((state) => state?.app);
  const [showFlowSelector, setShowFlowSelector] = useState(
    Boolean(data?.flow_id || data?.flow_identifier)
  );
  const [flowOptions, setFlowOptions] = useState([]);
  const [isFetchingFlows, setIsFetchingFlows] = useState(false);
  const [flowFetchError, setFlowFetchError] = useState(null);
  const [isFetchingFlowDetails, setIsFetchingFlowDetails] = useState(false);

  const storedIdentifier = useMemo(() => {
    if (data?.flow_identifier) return String(data.flow_identifier);
    if (data?.flow_id) return String(data.flow_id);
    if (data?.flow_uuid) return String(data.flow_uuid);
    return "";
  }, [data?.flow_identifier, data?.flow_id, data?.flow_uuid]);

  const selectedFlowOption = useMemo(() => {
    if (!storedIdentifier) return null;

    const matched = flowOptions.find(
      (option) => option.identifier === storedIdentifier
    );

    if (matched) return matched;

    const fallbackLabel =
      data?.flow_name ||
      data?.text ||
      (storedIdentifier ? `Flow ${storedIdentifier}` : null);

    if (!fallbackLabel) return null;

    return {
      label: fallbackLabel,
      value: storedIdentifier,
      identifier: storedIdentifier,
      data: null,
    };
  }, [flowOptions, storedIdentifier, data?.flow_name, data?.text]);

  const updateField = useCallback(
    (key, value) => {
      if (!allow || data?.[key] === value) return;
      updateButtons({
        updateKey: "update",
        id: data?.id,
        key: key,
        value: value,
      });
    },
    [allow, data, updateButtons]
  );

  const fetchPublishedFlows = useCallback(async () => {
    if (!currentBrand?.brand_id) {
      setFlowFetchError(null);
      return;
    }

    setIsFetchingFlows(true);
    setFlowFetchError(null);

    try {
      const response = await flowsList(currentBrand.brand_id, {
        status: "PUBLISHED",
        limit: 100,
      });

      const flows = response?.data?.flows || [];
      setFlowOptions(flows.map(formatFlowOption));
    } catch (error) {
      console.error("Failed to fetch published flows:", error);
      setFlowFetchError("Unable to load published flows. Try again.");
      message.error("Unable to load published flows. Please try again.");
    } finally {
      setIsFetchingFlows(false);
    }
  }, [currentBrand?.brand_id]);

  const fetchFlowDetails = useCallback(
    async (flow) => {
      if (!flow) return;

      const identifier = flow?.id ?? flow?.flow_id ?? storedIdentifier;
      if (!identifier) return;

      if (!currentBrand?.brand_id) {
        message.warning("Select a brand before choosing a flow.");
        return;
      }

      setIsFetchingFlowDetails(true);

      try {
        const response = await flowsGet(identifier, currentBrand.brand_id, true);
        const payload =
          response?.data?.flow ??
          response?.data?.data ??
          response?.data ??
          response;

        const flowJson = parseFlowJson(payload?.flow_json);
        const firstScreenId = resolveFirstScreenId(flowJson);

        updateField("flow_identifier", String(payload?.id ?? identifier ?? ""));
        updateField("flow_uuid", payload?.uuid ?? flow?.uuid ?? null);
        updateField("flow_id", payload?.flow_id ?? flow?.flow_id ?? null);
        updateField("flow_name", payload?.name ?? flow?.name ?? "");
        updateField("flow_action", payload?.flow_action ?? "NAVIGATE");
        updateField("navigate_screen", firstScreenId);
      } catch (error) {
        console.error("Failed to fetch flow details:", error);
        setFlowFetchError("Unable to load flow details. Try again.");
        message.error("Unable to load flow details. Please try again.");
      } finally {
        setIsFetchingFlowDetails(false);
      }
    },
    [
      currentBrand?.brand_id,
      storedIdentifier,
      updateField,
    ]
  );

  const handleUseExistingClick = useCallback(() => {
    if (!allow) return;
    setShowFlowSelector(true);
    if (flowOptions.length === 0) {
      fetchPublishedFlows();
    }
  }, [allow, flowOptions.length, fetchPublishedFlows]);

  const handleIconChange = useCallback(
    (option) => {
      if (!allow || !option) return;
      updateField("icon", option?.value);
    },
    [allow, updateField]
  );

  const handleFlowSelect = useCallback(
    (option) => {
      if (!allow || !option) return;
      const selectedFlow = option?.data || null;

      if (selectedFlow) {
        updateField("flow_name", selectedFlow?.name ?? "");
        updateField(
          "flow_identifier",
          String(selectedFlow?.id ?? selectedFlow?.flow_id ?? "")
        );
        updateField("flow_uuid", selectedFlow?.uuid ?? null);
        updateField("flow_id", selectedFlow?.flow_id ?? null);
        updateField("flow_action", "NAVIGATE");
      } else {
        updateField("flow_name", option?.label ?? "");
      }

      fetchFlowDetails(selectedFlow);
    },
    [allow, fetchFlowDetails, updateField]
  );

  const handleTextChange = useCallback(
    (value) => {
      if (!allow) return;
      updateField("text", value);
    },
    [allow, updateField]
  );

  const handleRemove = useCallback(() => {
    if (!allow) return;
    updateButtons({
      updateKey: "remove",
      id: data?.id,
    });
  }, [allow, data?.id, updateButtons]);

  useEffect(() => {
    if (showFlowSelector && flowOptions.length === 0) {
      fetchPublishedFlows();
    }
  }, [fetchPublishedFlows, flowOptions.length, showFlowSelector]);

  useEffect(() => {
    setFlowOptions([]);
    setFlowFetchError(null);
    if (showFlowSelector) {
      fetchPublishedFlows();
    }
  }, [currentBrand?.brand_id, showFlowSelector, fetchPublishedFlows]);

  useEffect(() => {
    if ((data?.flow_id || data?.flow_identifier) && flowOptions.length === 0) {
      setShowFlowSelector(true);
    }
  }, [data?.flow_id, data?.flow_identifier, flowOptions.length]);

  useEffect(() => {
    if (
      selectedFlowOption?.data === null &&
      showFlowSelector &&
      flowOptions.length > 0
    ) {
      const matched = flowOptions.find(
        (option) => option.identifier === storedIdentifier
      );
      if (matched) {
        fetchFlowDetails(matched?.data);
      }
    }
  }, [
    fetchFlowDetails,
    flowOptions,
    selectedFlowOption?.data,
    showFlowSelector,
    storedIdentifier,
  ]);

  const iconSelectedOption =
    FLOW_ICON_OPTIONS.find((option) => option.value === (data?.icon || "DOCUMENT")) ??
    FLOW_ICON_OPTIONS[0];

  return (
    <div className="flex flex-col gap-[0.5vw]">
      <div className="flex gap-[0.6vw] items-start">
        <div className="flex-1 grid grid-cols-[9.5vw_9.5vw_minmax(0,1fr)] gap-[0.6vw]">
          <div className="flex flex-col gap-[0.25vw]">
            <p className="font-sm weight-medium text-[var(--font-600)]">
              Type of Action
            </p>
            <Select
              options={optionsList}
              value={typeLabel}
              dropdownIcon={ICONS?.SelectDropdown}
              position="top"
              allow={allow}
              onChange={(item) => {
                if (allow && onTypeChange) onTypeChange(item);
              }}
            />
          </div>
          <div className="flex flex-col gap-[0.25vw]">
            <p className="font-sm weight-medium text-[var(--font-600)]">
              Button Icon
            </p>
            <Select
              options={FLOW_ICON_OPTIONS}
              value={iconSelectedOption}
              dropdownIcon={ICONS?.SelectDropdown}
              position="top"
              allow={allow}
              onChange={handleIconChange}
            />
          </div>
          <div className="flex flex-col gap-[0.25vw]">
            <p className="font-sm weight-medium text-[var(--font-600)]">
              Button Text
            </p>
            <BtnInput
              value={data?.text ?? ""}
              count={25}
              onChange={handleTextChange}
              disabled={!allow}
            />
          </div>
        </div>
        <button
          className={`w-[1.5vw] h-[1.5vw] group bg-transparent flex items-center justify-center rounded-[0.4vw] ${
            allow ? "hover:bg-red-200" : ""
          }`}
          onClick={handleRemove}
        >
          <Delete
            width="1.1vw"
            height="1.1vw"
            className={`stroke-[#898E99] ${
              allow ? " group-hover:stroke-red-500" : ""
            }`}
            color=""
          />
        </button>
      </div>
      <div className="flex flex-col gap-[0.4vw]">
        <div className="flex items-center gap-[0.5vw]">
          <button
            className={`px-[0.8vw] py-[0.4vw] border-[0.08vw] border-[var(--border-100)] rounded-[0.4vw] font-sm weight-semibold text-[var(--contentText)] ${
              allow ? "hover:bg-[var(--BG-25)]" : "opacity-50 cursor-not-allowed"
            }`}
            onClick={handleUseExistingClick}
          >
            Use existing
          </button>
          {showFlowSelector && (
            <div className="min-w-[14vw] max-w-[20vw]">
              <Select
                options={flowOptions}
                value={selectedFlowOption}
                placeholder="Select a published flow"
                dropdownIcon={ICONS?.SelectDropdown}
                position="top"
                allow={allow && !isFetchingFlows}
                onChange={handleFlowSelect}
                optionClassName="!items-center"
                optionsClassName="max-h-[20vw]"
              />
            </div>
          )}
        </div>
        {isFetchingFlows && (
          <p className="font-xs weight-medium text-[var(--font-400)]">
            Loading published flows...
          </p>
        )}
        {isFetchingFlowDetails && (
          <p className="font-xs weight-medium text-[var(--font-400)]">
            Fetching flow details...
          </p>
        )}
        {flowFetchError && (
          <p className="font-xs weight-medium text-[var(--fontRed)]">
            {flowFetchError}
          </p>
        )}
      </div>
    </div>
  );
};

export default FlowCTAButton;
